import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import { toast } from "sonner";
import {
    PackageOpen, Plus, X, Save, Trash2,
    Filter, Boxes, ArrowRight, AlertTriangle, Check, RefreshCcw, ShoppingCart,
} from "lucide-react";

const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "number") {
        if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
        if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
        if (Number.isInteger(v)) return v.toLocaleString("en-IN");
        return v.toFixed(2);
    }
    return String(v);
};

function IssueMaterialModal({ open, onClose, projects, items, taxonomy, onSaved }) {
    const [form, setForm] = useState({
        date: new Date().toISOString().slice(0, 10),
        project_id: "",
        item_id: "",
        qty_issued: "",
        purpose: "",
        issued_to: "",
        approver: "",
        notes: "",
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                date: new Date().toISOString().slice(0, 10),
                project_id: "", item_id: "", qty_issued: "",
                purpose: "", issued_to: "", approver: "", notes: "",
            });
        }
    }, [open]);

    const selectedItem = items.find((i) => i.id === form.item_id);
    const stockAvailable = selectedItem?.stock ?? 0;
    const unit = selectedItem?.unit ?? "";
    const rate = selectedItem?.rate ?? 0;
    const qtyNum = Number(form.qty_issued) || 0;
    const estValue = qtyNum * rate;
    const insufficient = qtyNum > stockAvailable;

    const submit = async () => {
        if (!form.project_id || !form.item_id || !form.qty_issued) {
            toast.error("Project, Item and Quantity are required");
            return;
        }
        if (insufficient) {
            toast.error(`Insufficient stock · available ${stockAvailable} ${unit}`);
            return;
        }
        setBusy(true);
        try {
            const r = await api.post("/consumption", {
                ...form,
                qty_issued: qtyNum,
            });
            toast.success(`Issued ${qtyNum} ${unit} · ${r.data.mi.voucher_no}`);
            onSaved?.(r.data);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-panel border border-panel max-w-lg w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-panel p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <PackageOpen className="text-[var(--brand-amber)]" size={16} />
                        <div className="font-display font-bold text-[var(--text-primary)]">Issue Material to Site (MIV)</div>
                    </div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X size={16} /></button>
                </div>
                <div className="p-4 space-y-3">
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Date</div>
                        <input
                            data-testid="consumption-date"
                            type="date"
                            value={form.date}
                            onChange={(e) => setForm({ ...form, date: e.target.value })}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Project *</div>
                        <select
                            data-testid="consumption-project"
                            value={form.project_id}
                            onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--text-primary)]"
                        >
                            <option value="">— Select project —</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">
                            Item *  {selectedItem && (
                                <span className="text-[var(--text-muted)] ml-2">
                                    Stock: <span className="text-[var(--pos)]">{stockAvailable}</span> {unit} @ ₹{rate}
                                </span>
                            )}
                        </div>
                        <select
                            data-testid="consumption-item"
                            value={form.item_id}
                            onChange={(e) => setForm({ ...form, item_id: e.target.value })}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--text-primary)]"
                        >
                            <option value="">— Select material —</option>
                            {items.map((i) => (
                                <option key={i.id} value={i.id}>
                                    {i.item} · Stock {i.stock} {i.unit}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">
                                Quantity * {unit && <span className="text-[var(--text-muted)]">({unit})</span>}
                            </div>
                            <input
                                data-testid="consumption-qty"
                                type="number"
                                step="0.01"
                                value={form.qty_issued}
                                onChange={(e) => setForm({ ...form, qty_issued: e.target.value })}
                                className={`w-full bg-[var(--bg-base)] border ${insufficient ? "border-[var(--neg)]" : "border-panel"} px-3 py-2 text-xs font-mono-fin`}
                            />
                            {insufficient && (
                                <div className="text-[10px] text-[var(--neg)] mt-1 font-mono-fin">
                                    ⚠ Only {stockAvailable} {unit} in stock
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Est. Value</div>
                            <div data-testid="consumption-value" className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--brand-amber)]">
                                {fmt(estValue)}
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Purpose</div>
                        <select
                            data-testid="consumption-purpose"
                            value={form.purpose}
                            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--text-primary)]"
                        >
                            <option value="">— Select purpose —</option>
                            {(taxonomy?.consumption_purposes || []).map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Issued To (Site Team / Contractor)</div>
                        <input
                            data-testid="consumption-issued-to"
                            value={form.issued_to}
                            onChange={(e) => setForm({ ...form, issued_to: e.target.value })}
                            placeholder="e.g., Ram Singh Rathore (V-05)"
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Approver</div>
                        <input
                            data-testid="consumption-approver"
                            value={form.approver}
                            onChange={(e) => setForm({ ...form, approver: e.target.value })}
                            placeholder="e.g., Rakesh Verma (Project Head)"
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Notes</div>
                        <textarea
                            data-testid="consumption-notes"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={2}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-body"
                        />
                    </div>
                    <button
                        data-testid="consumption-save"
                        onClick={submit}
                        disabled={busy || insufficient}
                        className="w-full bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        <Save size={12} /> {busy ? "Posting…" : "Issue Material & Post to Project Cost"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function Consumption() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [projects, setProjects] = useState([]);
    const [items, setItems] = useState([]);
    const [taxonomy, setTaxonomy] = useState(null);
    const [filterProject, setFilterProject] = useState("");
    const [filterItem, setFilterItem] = useState("");
    const [issueOpen, setIssueOpen] = useState(false);
    const [reorderAlerts, setReorderAlerts] = useState({ alerts: [], counts: {} });
    const [drafts, setDrafts] = useState([]);
    const [draftSummary, setDraftSummary] = useState({});

    const load = async () => {
        const params = {};
        if (filterProject) params.project_id = filterProject;
        if (filterItem) params.item_id = filterItem;
        try {
            const [c, s, p, i, t, ra, rd] = await Promise.all([
                api.get("/consumption", { params }),
                api.get("/consumption/summary"),
                api.get("/data/projects"),
                api.get("/data/inventory"),
                api.get("/settings/taxonomy"),
                api.get("/consumption/reorder-alerts"),
                api.get("/consumption/reorder-drafts"),
            ]);
            setRows(c.data.items || []);
            setSummary({ ...c.data.summary, org: s.data });
            setProjects(p.data.items || []);
            setItems(i.data.items || []);
            setTaxonomy(t.data);
            setReorderAlerts(ra.data);
            setDrafts(rd.data.drafts || []);
            setDraftSummary(rd.data.summary || {});
        } catch (e) {
            toast.error("Load failed: " + e.message);
        }
    };

    useEffect(() => { load(); }, [filterProject, filterItem]);

    const reverse = async (miId) => {
        if (!window.confirm(`Reverse MIV ${miId}? Stock will go back and project cost will be reduced.`)) return;
        try {
            await api.delete(`/consumption/${miId}`);
            toast.success(`Reversed ${miId} · stock restored`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    // Called from IssueMaterialModal on save-success so we can surface auto-reorder toast
    const onMIVSaved = (resp) => {
        if (resp?.reorder_draft) {
            const rd = resp.reorder_draft;
            toast.warning(
                `🔔 Auto Reorder Draft ${rd.draft_no} — ${rd.suggested_qty} ${rd.unit} for ${rd.vendor_name || "unassigned vendor"} · ₹${(rd.grand_total || 0).toLocaleString("en-IN")}`,
                { duration: 6000 }
            );
        }
        load();
    };

    const approveDraft = async (id) => {
        const approver = window.prompt("Enter your name to approve this draft:", "");
        if (!approver) return;
        try {
            await api.post(`/consumption/reorder-drafts/${id}/approve`, { approver });
            toast.success(`Draft approved · ready for PO conversion`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    const rejectDraft = async (id) => {
        if (!window.confirm(`Delete draft ${id}?`)) return;
        try {
            await api.delete(`/consumption/reorder-drafts/${id}`);
            toast.success(`Draft removed`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    const convertDraftToPO = async (id) => {
        if (!window.confirm(`Convert draft ${id} into a real Purchase Order? This will create a PO in the Procurement module and email it to the vendor.`)) return;
        try {
            const r = await api.post(`/consumption/reorder-drafts/${id}/convert-to-po`);
            const po = r.data.po;
            const em = r.data.email || {};
            toast.success(`PO ${po.po_no} created · ₹${(po.total_value || 0).toLocaleString("en-IN")}`);
            // Follow-up email status toast
            setTimeout(() => {
                if (em.sent) {
                    toast.success(`📧 PO emailed to ${em.recipient}`, { duration: 5000 });
                } else {
                    toast.warning(`⚠ PO not emailed: ${em.reason || "unknown"}`, { duration: 6000 });
                }
            }, 800);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    const generateManualDraft = async (alert) => {
        try {
            const r = await api.post(`/consumption/reorder-drafts`, {
                item_id: alert.item_id,
                qty: alert.suggested_qty,
                vendor_id: alert.preferred_vendor_id,
                notes: `Manual draft · triggered from low-stock alert (${alert.shortfall_pct}% below reorder)`,
            });
            toast.success(`Draft ${r.data.draft.draft_no} created`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    const perProject = summary?.org?.per_project || [];
    const perItem = summary?.org?.per_item || [];

    const projectMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// MATERIAL CONSUMPTION</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3">
                    <PackageOpen className="text-[var(--brand-amber)]" size={26} />
                    Material Issue & Consumption
                </h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    Purchase → GRN → Inventory → <span className="text-[var(--brand-amber)]">Consumption (MIV)</span> → Project Actual Cost
                </p>
            </div>

            {/* KPI Bar */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="consumption-kpis">
                    <Panel testId="kpi-consumed">
                        <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider">Total Consumed (Filtered)</div>
                        <div className="mt-1 text-2xl font-display font-black text-[var(--text-primary)]">{fmt(summary.total_value)}</div>
                        <div className="text-[10px] font-mono-fin text-[var(--text-muted)] mt-1">{summary.count} vouchers</div>
                    </Panel>
                    <Panel testId="kpi-org-value">
                        <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider">Org-wide Consumed</div>
                        <div className="mt-1 text-2xl font-display font-black text-[var(--text-primary)]">{fmt(summary.org?.total_value || 0)}</div>
                        <div className="text-[10px] font-mono-fin text-[var(--text-muted)] mt-1">{summary.org?.voucher_count || 0} MIVs org-wide</div>
                    </Panel>
                    <Panel testId="kpi-boq-variance">
                        <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider">Avg BOQ Variance</div>
                        <div className={`mt-1 text-2xl font-display font-black ${(summary.avg_variance_pct || 0) > 5 ? "text-[var(--neg)]" : "text-[var(--pos)]"}`}>
                            {(summary.avg_variance_pct || 0) > 0 ? "+" : ""}{(summary.avg_variance_pct || 0).toFixed(1)}%
                        </div>
                        <div className="text-[10px] font-mono-fin text-[var(--text-muted)] mt-1">vs BOQ norm</div>
                    </Panel>
                    <Panel testId="kpi-over-boq">
                        <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider">Over-BOQ Vouchers</div>
                        <div className={`mt-1 text-2xl font-display font-black ${summary.over_boq_count > 0 ? "text-[var(--neg)]" : "text-[var(--text-primary)]"}`}>
                            {summary.over_boq_count || 0}
                        </div>
                        <div className="text-[10px] font-mono-fin text-[var(--text-muted)] mt-1">variance &gt; 5%</div>
                    </Panel>
                </div>
            )}

            {/* Actions & Filters */}
            <div className="flex flex-wrap items-center gap-2">
                <button
                    data-testid="issue-material-btn"
                    onClick={() => setIssueOpen(true)}
                    className="bg-[var(--brand-amber)] text-black px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors flex items-center gap-1.5"
                >
                    <Plus size={12} /> Issue Material to Site
                </button>
                <div className="flex items-center gap-1.5 border border-panel bg-[var(--bg-base)] px-2 py-1">
                    <Filter size={12} className="text-[var(--text-secondary)]" />
                    <select
                        data-testid="filter-project"
                        value={filterProject}
                        onChange={(e) => setFilterProject(e.target.value)}
                        className="bg-transparent text-[11px] font-mono-fin px-1 py-0.5 focus:outline-none min-w-[180px]"
                    >
                        <option value="">All projects</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name?.slice(0, 30)}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-1.5 border border-panel bg-[var(--bg-base)] px-2 py-1">
                    <Boxes size={12} className="text-[var(--text-secondary)]" />
                    <select
                        data-testid="filter-item"
                        value={filterItem}
                        onChange={(e) => setFilterItem(e.target.value)}
                        className="bg-transparent text-[11px] font-mono-fin px-1 py-0.5 focus:outline-none min-w-[180px]"
                    >
                        <option value="">All items</option>
                        {items.map((i) => <option key={i.id} value={i.id}>{i.item?.slice(0, 40)}</option>)}
                    </select>
                </div>
                {(reorderAlerts.counts?.items_below_reorder > 0) && (
                    <div
                        data-testid="reorder-badge"
                        className="border border-[var(--neg)] bg-[var(--neg)]/10 px-2 py-1 text-[11px] font-mono-fin uppercase tracking-wider text-[var(--neg)] flex items-center gap-1.5"
                    >
                        <AlertTriangle size={12} />
                        {reorderAlerts.counts.items_below_reorder} Items Below Reorder
                        {reorderAlerts.counts.uncovered > 0 && (
                            <span className="text-[var(--brand-amber)] ml-1">· {reorderAlerts.counts.uncovered} need action</span>
                        )}
                    </div>
                )}
                <button
                    data-testid="refresh-btn"
                    onClick={load}
                    title="Refresh"
                    className="border border-panel px-2 py-1 text-[var(--text-secondary)] hover:text-[var(--brand-amber)] hover:border-[var(--brand-amber)] transition-colors"
                >
                    <RefreshCcw size={12} />
                </button>
            </div>

            {/* Flow diagram */}
            <Panel testId="flow-diagram" title="Material Flow · How Consumption Impacts P&L">
                <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono-fin p-2">
                    <div className="border border-panel px-3 py-2">
                        <div className="text-[9px] uppercase text-[var(--text-muted)]">1. Purchase</div>
                        <div className="text-[var(--text-primary)]">Vendor Bill / PO</div>
                        <div className="text-[10px] text-[var(--text-secondary)] mt-1">Cr: Payables · Dr: Inventory</div>
                    </div>
                    <ArrowRight size={14} className="text-[var(--brand-amber)]" />
                    <div className="border border-panel px-3 py-2">
                        <div className="text-[9px] uppercase text-[var(--text-muted)]">2. GRN</div>
                        <div className="text-[var(--text-primary)]">Stock IN</div>
                        <div className="text-[10px] text-[var(--text-secondary)] mt-1">Stock ↑</div>
                    </div>
                    <ArrowRight size={14} className="text-[var(--brand-amber)]" />
                    <div className="border border-[var(--brand-amber)] px-3 py-2 bg-[var(--brand-amber-dim)]">
                        <div className="text-[9px] uppercase text-[var(--brand-amber)]">3. MIV · Consumption</div>
                        <div className="text-[var(--text-primary)]">Issue to Project Site</div>
                        <div className="text-[10px] text-[var(--text-secondary)] mt-1">Stock ↓ · Project Actual Cost ↑</div>
                    </div>
                    <ArrowRight size={14} className="text-[var(--brand-amber)]" />
                    <div className="border border-panel px-3 py-2">
                        <div className="text-[9px] uppercase text-[var(--text-muted)]">4. Vendor Payment</div>
                        <div className="text-[var(--text-primary)]">Bank/Cash → Vendor</div>
                        <div className="text-[10px] text-[var(--text-secondary)] mt-1">Bank ↓ · Payables ↓</div>
                    </div>
                </div>
            </Panel>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Per Project */}
                <Panel testId="per-project" title="Consumption by Project (Org-wide)">
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs font-body">
                            <thead>
                                <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2 px-2">Project</th>
                                    <th className="text-right py-2 px-2">Vouchers</th>
                                    <th className="text-right py-2 px-2">Consumed</th>
                                    <th className="text-right py-2 px-2">vs Budget</th>
                                </tr>
                            </thead>
                            <tbody>
                                {perProject.length === 0 && <tr><td colSpan={4} className="text-center py-4 text-[var(--text-muted)]">No consumption yet.</td></tr>}
                                {perProject.map((r) => {
                                    const p = projectMap[r.project_id];
                                    const budget = p?.approved_budget || 0;
                                    const pct = budget ? (r.total_value / budget * 100) : 0;
                                    return (
                                        <tr key={r.project_id} className="border-b border-panel/60">
                                            <td className="py-1.5 px-2">
                                                <div className="text-[var(--brand-amber)] font-mono-fin">{r.code}</div>
                                                <div className="text-[10px] text-[var(--text-primary)]/60">{r.name?.slice(0, 40)}</div>
                                            </td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">{r.voucher_count}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--text-primary)]">{fmt(r.total_value)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">
                                                <span className={pct > 5 ? "text-[var(--pos)]" : "text-[var(--text-secondary)]"}>{pct.toFixed(1)}%</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Panel>

                {/* Per Item */}
                <Panel testId="per-item" title="Top Materials Consumed">
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs font-body">
                            <thead>
                                <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2 px-2">Item</th>
                                    <th className="text-right py-2 px-2">Qty</th>
                                    <th className="text-right py-2 px-2">Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {perItem.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-[var(--text-muted)]">No consumption yet.</td></tr>}
                                {perItem.slice(0, 12).map((r) => (
                                    <tr key={r.item_id} className="border-b border-panel/60">
                                        <td className="py-1.5 px-2 text-[var(--text-primary)]/90">{r.item_name}</td>
                                        <td className="text-right py-1.5 px-2 font-mono-fin">{r.total_qty} {r.unit}</td>
                                        <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{fmt(r.total_value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>

            {/* ==================== REORDER ALERTS + DRAFTS ==================== */}
            {(reorderAlerts.counts?.items_below_reorder > 0 || drafts.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Low Stock Alerts */}
                    <Panel
                        testId="reorder-alerts"
                        title={`Reorder Alerts · ${reorderAlerts.counts?.items_below_reorder || 0} items below reorder`}
                        subtitle={`${reorderAlerts.counts?.covered || 0} covered by open drafts · ${reorderAlerts.counts?.uncovered || 0} need action`}
                    >
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs font-body">
                                <thead>
                                    <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                        <th className="text-left py-2 px-2">Item</th>
                                        <th className="text-right py-2 px-2">Stock</th>
                                        <th className="text-right py-2 px-2">Reorder</th>
                                        <th className="text-right py-2 px-2">Shortfall</th>
                                        <th className="text-left py-2 px-2">Preferred Vendor</th>
                                        <th className="text-right py-2 px-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(reorderAlerts.alerts || []).length === 0 && (
                                        <tr><td colSpan={6} className="text-center py-4 text-[var(--text-muted)]">All stock levels above reorder point ✓</td></tr>
                                    )}
                                    {(reorderAlerts.alerts || []).map((a) => (
                                        <tr key={a.item_id} data-testid={`alert-${a.item_id}`} className="border-b border-panel/60">
                                            <td className="py-1.5 px-2">
                                                <div className="text-[var(--text-primary)]/90">{a.item_name}</div>
                                                <div className="text-[10px] text-[var(--text-primary)]/50">{a.item_id}</div>
                                            </td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--neg)]">{a.stock} {a.unit}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--text-primary)]/70">{a.reorder}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">
                                                <span className={a.shortfall_pct > 50 ? "text-[var(--neg)]" : "text-[var(--brand-amber)]"}>{a.shortfall_pct}%</span>
                                            </td>
                                            <td className="py-1.5 px-2 text-[var(--text-primary)]/80 text-[11px]">{a.preferred_vendor_name || <span className="text-[var(--text-muted)]">— none —</span>}</td>
                                            <td className="text-right py-1.5 px-2">
                                                {a.has_open_draft ? (
                                                    <span className="text-[var(--pos)] text-[10px] font-mono-fin uppercase">Draft Open</span>
                                                ) : (
                                                    <button
                                                        data-testid={`gen-draft-${a.item_id}`}
                                                        onClick={() => generateManualDraft(a)}
                                                        className="border border-[var(--brand-amber)] text-[var(--brand-amber)] px-2 py-0.5 text-[10px] font-mono-fin uppercase tracking-wider hover:bg-[var(--brand-amber)] hover:text-black transition-colors flex items-center gap-1"
                                                    >
                                                        <ShoppingCart size={10} /> Generate Draft
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    {/* Reorder Drafts (auto + manual) */}
                    <Panel
                        testId="reorder-drafts"
                        title={`Reorder Draft POs · ${drafts.length}`}
                        subtitle={`₹${((draftSummary?.total_value || 0) / 100000).toFixed(2)} L total · auto-generated when stock drops below reorder`}
                    >
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs font-body">
                                <thead>
                                    <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                        <th className="text-left py-2 px-2">Draft No</th>
                                        <th className="text-left py-2 px-2">Item</th>
                                        <th className="text-right py-2 px-2">Qty</th>
                                        <th className="text-left py-2 px-2">Vendor</th>
                                        <th className="text-right py-2 px-2">Est. Total</th>
                                        <th className="text-left py-2 px-2">Status</th>
                                        <th className="text-right py-2 px-2">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drafts.length === 0 && <tr><td colSpan={7} className="text-center py-4 text-[var(--text-muted)]">No open drafts.</td></tr>}
                                    {drafts.map((d) => (
                                        <tr key={d.id} data-testid={`draft-row-${d.id}`} className="border-b border-panel/60">
                                            <td className="py-1.5 px-2">
                                                <div className="text-[var(--brand-amber)] font-mono-fin">{d.draft_no}</div>
                                                <div className="text-[9px] text-[var(--text-primary)]/50">{d.trigger === "auto_from_miv" ? "AUTO" : "MANUAL"}</div>
                                            </td>
                                            <td className="py-1.5 px-2 text-[var(--text-primary)]/90 text-[11px]">{d.item_name}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">{d.suggested_qty} {d.unit}</td>
                                            <td className="py-1.5 px-2 text-[var(--text-primary)]/80 text-[11px]">{d.vendor_name || <span className="text-[var(--text-muted)]">— unassigned —</span>}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{fmt(d.grand_total)}</td>
                                            <td className="py-1.5 px-2 text-[10px] font-mono-fin uppercase">
                                                <span className={
                                                    d.status === "approved"
                                                        ? "text-[var(--pos)]"
                                                        : d.status === "converted"
                                                            ? "text-blue-400"
                                                            : d.status === "rejected"
                                                                ? "text-[var(--neg)]"
                                                                : "text-[var(--brand-amber)]"
                                                }>{d.status}{d.po_no ? ` · ${d.po_no}` : ""}</span>
                                            </td>
                                            <td className="text-right py-1.5 px-2 whitespace-nowrap">
                                                {d.status === "draft" && (
                                                    <button
                                                        data-testid={`approve-draft-${d.id}`}
                                                        onClick={() => approveDraft(d.id)}
                                                        title="Approve draft"
                                                        className="text-[var(--pos)] hover:text-[var(--text-primary)] p-1"
                                                    >
                                                        <Check size={12} />
                                                    </button>
                                                )}
                                                {d.status === "approved" && (
                                                    <button
                                                        data-testid={`convert-po-${d.id}`}
                                                        onClick={() => convertDraftToPO(d.id)}
                                                        title="Convert to real Purchase Order"
                                                        className="text-[var(--brand-amber)] hover:text-[var(--text-primary)] p-1 border border-[var(--brand-amber)] text-[9px] font-mono-fin uppercase px-1"
                                                    >
                                                        → PO
                                                    </button>
                                                )}
                                                {d.status !== "converted" && (
                                                    <button
                                                        data-testid={`reject-draft-${d.id}`}
                                                        onClick={() => rejectDraft(d.id)}
                                                        title="Reject / remove draft"
                                                        className="text-[var(--neg)] hover:text-[var(--text-primary)] p-1"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </div>
            )}

            {/* MIV list */}
            <Panel testId="miv-list" title={`Material Issue Vouchers · ${rows.length}`} subtitle="Reverse a voucher to put stock back and reduce project cost">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs font-body">
                        <thead>
                            <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-left py-2 px-2">MIV No</th>
                                <th className="text-left py-2 px-2">Date</th>
                                <th className="text-left py-2 px-2">Project</th>
                                <th className="text-left py-2 px-2">Material</th>
                                <th className="text-right py-2 px-2">Qty</th>
                                <th className="text-right py-2 px-2">Value</th>
                                <th className="text-left py-2 px-2">Purpose</th>
                                <th className="text-left py-2 px-2">Approver</th>
                                <th className="text-right py-2 px-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={9} className="text-center py-8 text-[var(--text-muted)]">
                                    No material issued. Click <span className="text-[var(--brand-amber)]">Issue Material to Site</span> to start.
                                </td></tr>
                            )}
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={`miv-row-${r.id}`} className="border-b border-panel/60 hover:bg-panel/40">
                                    <td className="py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{r.voucher_no}</td>
                                    <td className="py-1.5 px-2 font-mono-fin">{r.date}</td>
                                    <td className="py-1.5 px-2">
                                        <div className="text-[var(--text-primary)] font-mono-fin">{r.project_code || r.project_id}</div>
                                        <div className="text-[10px] text-[var(--text-primary)]/60">{r.project_name?.slice(0, 32)}</div>
                                    </td>
                                    <td className="py-1.5 px-2 text-[var(--text-primary)]/90">{r.item_name}</td>
                                    <td className="text-right py-1.5 px-2 font-mono-fin">{r.qty_issued} {r.unit}</td>
                                    <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{fmt(r.value)}</td>
                                    <td className="py-1.5 px-2 text-[var(--text-primary)]/80 text-[11px]">{r.purpose || "—"}</td>
                                    <td className="py-1.5 px-2 text-[var(--text-primary)]/80 text-[11px]">{r.approver || "—"}</td>
                                    <td className="text-right py-1.5 px-2">
                                        <button
                                            data-testid={`reverse-miv-${r.id}`}
                                            onClick={() => reverse(r.id)}
                                            title="Reverse MIV · restore stock"
                                            className="text-[var(--neg)] hover:text-[var(--text-primary)] p-1"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>

            <IssueMaterialModal
                open={issueOpen}
                onClose={() => setIssueOpen(false)}
                projects={projects}
                items={items}
                taxonomy={taxonomy}
                onSaved={onMIVSaved}
            />
        </div>
    );
}
